import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";

const StockAdjustmentModal = ({
  show,
  onHide,
  onSubmit,
  locations,
  items,
  itemsLoading,
  selectedLocationId,
  loading,
  error,
  availableQuantity,
  onSearchItems,
  isAdmin,
}) => {
  const [locationId, setLocationId] = useState(selectedLocationId || "");
  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Update locationId when selectedLocationId prop changes
  useEffect(() => {
    if (selectedLocationId) {
      setLocationId(selectedLocationId);
    }
  }, [selectedLocationId]);

  // Debounced search for items
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        onSearchItems(searchTerm);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, onSearchItems]);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setItemId("");
      setQuantity(1);
      setReason("");
      setSearchTerm("");
      if (selectedLocationId) {
        setLocationId(selectedLocationId);
      }
    }
  }, [show, selectedLocationId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isAdmin && !locationId) return;
    if (!itemId || quantity <= 0 || !reason.trim()) return;

    const data = {
      item_id: parseInt(itemId),
      quantity: parseFloat(quantity),
      reason: reason.trim(),
    };
    if (isAdmin && locationId) {
      data.location_id = parseInt(locationId);
    }
    onSubmit(data);
  };

  const isValid = (!isAdmin || locationId) && itemId && quantity > 0 && reason.trim();

  return (
    <Modal show={show} onHide={onHide} size="lg" dialogClassName="modal-logut" animation={false}>
      <div className="card mb-0">
        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#fabb3d', color: '#34383c' }}>
          <span>
            <i className="fa fa-edit mr-05"></i>
            Adjust Stock
          </span>
          <button type="button" className="btn-close" onClick={onHide}></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card-body">
            <div className="alert alert-info py-2 mb-3">
              <i className="fa fa-info-circle mr-05"></i>
              Use this form to manually adjust stock for items that cannot be scanned (e.g., pre-deployment inventory, damaged labels).
            </div>

            {error && (
              <div className="alert alert-danger py-2 mb-3">
                <i className="fa fa-exclamation-circle mr-05"></i>
                {error}
                {availableQuantity !== null && availableQuantity !== undefined && (
                  <div className="mt-2">
                    <strong>Available quantity: {availableQuantity}</strong>
                    <br />
                    <small>Please reduce the quantity to {availableQuantity} or less.</small>
                  </div>
                )}
              </div>
            )}

            <div className="row">
              {/* Location - Admin only */}
              {isAdmin && (
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label">
                    <i className="fa fa-map-marker mr-05"></i>
                    Location <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    disabled={loading}
                    required
                  >
                    <option value="">-- Select Location --</option>
                    {Array.isArray(locations) && locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Quantity */}
              <div className={`col-12 ${isAdmin ? 'col-md-6' : 'col-md-12'} mb-3`}>
                <label className="form-label">
                  <i className="fa fa-hashtag mr-05"></i>
                  Quantity <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  min="0.1"
                  step="0.1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* Item Search */}
            <div className="mb-3">
              <label className="form-label">
                <i className="fa fa-cube mr-05"></i>
                Item <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Search items by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
              {itemsLoading ? (
                <div className="text-muted small">
                  <i className="fa fa-spinner fa-spin mr-05"></i> Searching...
                </div>
              ) : items && items.length > 0 ? (
                <select
                  className="form-control"
                  value={itemId}
                  onChange={(e) => setItemId(e.target.value)}
                  disabled={loading}
                  size="5"
                  required
                >
                  <option value="">-- Select an Item --</option>
                  {items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} {item.measure_unit ? `(${item.measure_unit})` : ''}
                    </option>
                  ))}
                </select>
              ) : searchTerm.length >= 2 ? (
                <div className="text-muted small">No items found matching "{searchTerm}"</div>
              ) : (
                <div className="text-muted small">Type at least 2 characters to search</div>
              )}
            </div>

            {/* Reason */}
            <div className="mb-3">
              <label className="form-label">
                <i className="fa fa-question-circle mr-05"></i>
                Reason <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., Consumed before tracking system launch"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="card-footer d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-secondary mr-05"
              onClick={onHide}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !isValid}
            >
              {loading ? (
                <>
                  <i className="fa fa-spinner fa-spin mr-05"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="fa fa-check mr-05"></i>
                  Adjust Stock
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default StockAdjustmentModal;
